import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Tabs, 
  Tab, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  IconButton 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`predetermined-tabpanel-${index}`}
      aria-labelledby={`predetermined-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  maxHeight: '80vh',
  overflowY: 'auto',
  padding: 20,
  boxShadow: 24,
  outline: 'none',
  backgroundColor: '#fff',
  borderRadius: '8px'
};

const ComentariosResultadosModal = ({ open, onClose, Analito, onSave }) => {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();
  
  // Estado para la pestaña activa (0: Comentarios, 1: Resultados)
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para Comentarios Predeterminados
  const [comentarioInput, setComentarioInput] = useState('');
  const [comentarios, setComentarios] = useState([]);
  // Estado para almacenar los comentarios eliminados
  const [deletedComentarios, setDeletedComentarios] = useState([]);
  
  // Estados para Resultados Predeterminados
  const [resultadoInput, setResultadoInput] = useState('');
  const [resultados, setResultados] = useState([]);
  // Nuevo estado para almacenar los resultados eliminados
  const [deletedResultados, setDeletedResultados] = useState([]);
  
  // Al abrir el modal, cargar comentarios y resultados existentes para el analito
  useEffect(() => {
    if (open && Analito) {
      // Cargar comentarios
      const fetchComentarios = async () => {
        try {
          const response = await fetch(`${server}/MgAnalitoComentariosPredeterminadoes/porAnalito/${Analito.idAnalito}`);
          if (response.ok) {
            const data = await response.json();
            const comentariosCargados = data.$values || data || [];
            setComentarios(comentariosCargados);
            // Reiniciar la lista de eliminados al abrir el modal
            setDeletedComentarios([]);
          } else {
            console.error('Error al cargar comentarios:', response.statusText);
          }
        } catch (error) {
          console.error('Error al cargar comentarios:', error);
        }
      };
      // Cargar resultados predeterminados (suponiendo una API similar)
      const fetchResultados = async () => {
        try {
          const response = await fetch(`${server}/MgAnalitoResultadosPredeterminadoes/porAnalito/${Analito.idAnalito}`);
          if (response.ok) {
            const data = await response.json();
            const resultadosCargados = data.$values || data || [];
            setResultados(resultadosCargados);
            setDeletedResultados([]);
          } else {
            console.error('Error al cargar resultados:', response.statusText);
          }
        } catch (error) {
          console.error('Error al cargar resultados:', error);
        }
      };
      fetchComentarios();
      fetchResultados();
    }
  }, [open, Analito, server]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Función para agregar un comentario nuevo
  const handleAgregarComentario = () => {
    if (comentarioInput.trim() === '') return;
    const nuevoComentario = { comentarioPredeterminado: comentarioInput.trim() };
    setComentarios(prev => [...prev, nuevoComentario]);
    setComentarioInput('');
  };

  // Función para eliminar un comentario con confirmación
  const handleDeleteComentario = (indexToDelete) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción eliminará el comentario.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setComentarios(prevComentarios => {
          const commentToDelete = prevComentarios[indexToDelete];
          if (commentToDelete && commentToDelete.id) {
            setDeletedComentarios(prev => [...prev, commentToDelete]);
          }
          return prevComentarios.filter((_, index) => index !== indexToDelete);
        });
        // Swal.fire('Eliminado', 'El comentario ha sido eliminado.', 'success');
      }
    });
  };

  // Función para agregar un resultado nuevo
  const handleAgregarResultado = () => {
    if (resultadoInput.trim() === '') return;
    const nuevoResultado = { resultadoPredeterminado: resultadoInput.trim() };
    setResultados(prev => [...prev, nuevoResultado]);
    setResultadoInput('');
  };
  
  // Función para eliminar un resultado con confirmación
  const handleDeleteResultado = (indexToDelete) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción eliminará el resultado.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setResultados(prevResultados => {
          const resultToDelete = prevResultados[indexToDelete];
          if (resultToDelete && resultToDelete.id) {
            setDeletedResultados(prev => [...prev, resultToDelete]);
          }
          return prevResultados.filter((_, index) => index !== indexToDelete);
        });
        // Swal.fire('Eliminado', 'El resultado ha sido eliminado.', 'success');
      }
    });
  };
  
  // Función para guardar comentarios que no tienen id (nuevos)
  const guardarComentarios = async () => {
    for (const c of comentarios) {
      if (!c.id) {
        const payload = {
          comentarioPredeterminado: c.comentarioPredeterminado,
          ingresadoPor: user?.codigoUsuario || 'ADMIN',
          idAnalito: Analito.idAnalito
        };
        const response = await fetch(`${server}/MgAnalitoComentariosPredeterminadoes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          console.error('Error al guardar un comentario:', response.statusText);
        }
      }
    }
  };
  
  // Función para guardar resultados que no tienen id (nuevos)
  const guardarResultados = async () => {
    for (const r of resultados) {
      if (!r.id) {
        const payload = {
          resultadoPredeterminado: r.resultadoPredeterminado,
          ingresadoPor: user?.codigoUsuario || 'ADMIN',
          idAnalito: Analito.idAnalito
        };
        const response = await fetch(`${server}/MgAnalitoResultadosPredeterminadoes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          console.error('Error al guardar un resultado:', response.statusText);
        }
      }
    }
  };
  
  // Función para eliminar los comentarios que fueron removidos
  const eliminarComentarios = async () => {
    const username = '' + user.codigoUsuario + '/';
    for (const c of deletedComentarios) {
      try {
        const response = await fetch(`${server}/MgAnalitoComentariosPredeterminadoes/${c.id}/${username}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
          console.error('Error al eliminar un comentario:', response.statusText);
        }
      } catch (error) {
        console.error('Error en la eliminación del comentario:', error);
      }
    }
  };
  
  // Función para eliminar los resultados que fueron removidos
  const eliminarResultados = async () => {
    const username = '' + user.codigoUsuario + '/';
    for (const r of deletedResultados) {
      try {
        const response = await fetch(`${server}/MgAnalitoResultadosPredeterminadoes/${r.id}/${username}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
          console.error('Error al eliminar un resultado:', response.statusText);
        }
      } catch (error) {
        console.error('Error en la eliminación del resultado:', error);
      }
    }
  };
  
  // Función combinada de guardar
  const handleGuardar = async () => {
    await guardarComentarios();
    await guardarResultados();
    await eliminarComentarios();
    await eliminarResultados();
    if (onSave) onSave({ comentarios, resultados });
    onClose();
  };
  
  return (
    <Modal open={open} onClose={onClose}>
      <Paper style={modalStyle}>
        <Typography variant="h6" gutterBottom>
          {Analito ? `Predeterminados para el analito: ${Analito.analito}` : 'Sin analito'}
        </Typography>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Pestañas de predeterminados">
          <Tab label="Comentarios" />
          <Tab label="Resultados" />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Comentario"
                fullWidth
                value={comentarioInput}
                onChange={(e) => setComentarioInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleAgregarComentario}>
                Agregar Comentario
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comentario</TableCell>
                      <TableCell>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comentarios.map((c, index) => (
                      <TableRow key={index}>
                        <TableCell>{c.comentarioPredeterminado}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleDeleteComentario(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Resultado Predeterminado"
                fullWidth
                value={resultadoInput}
                onChange={(e) => setResultadoInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleAgregarResultado}>
                Agregar Resultado
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Resultado</TableCell>
                      <TableCell>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultados.map((r, index) => (
                      <TableRow key={index}>
                        <TableCell>{r.resultadoPredeterminado}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleDeleteResultado(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </TabPanel>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Button variant="contained" color="secondary" onClick={handleGuardar}>
              Guardar Predeterminados
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
};

export default ComentariosResultadosModal;
